import dayjs from "dayjs"

export function take(value: string | null | undefined, n: number): string {
  if (!value) return ""
  return value.slice(0, n)
}

export function pad(num: number | null | undefined, length: number): string {
  if (num == null) return ""
  return String(num).padStart(length, "0")
}

export function formatDate(date: Date | string | null | undefined, format: string): string {
  if (!date) return ""
  return dayjs(date).format(format)
}

export function getInitials(first?: string | null, last?: string | null, len = 1) {
  return (first ? take(first, len) : "") + (last ? take(last, len) : "")
}

export type TokenHandlers<T> = Record<string, (context: T) => string>

export type TokenFn<T> = (t: T) => string

export function formatPattern<T>(pattern: string, context: T, tokens: TokenHandlers<T>): string {
  return pattern.replace(/\[(.*?)\]/g, (_, token: string) => {
    const handler = tokens[token]
    return handler ? handler(context) : `[${token}]`
  })
}

export type ChildInfo = {
  firstName?: string | null
  lastName?: string | null
  childId?: number | null
  birthdate?: Date | null
}

export type EmployeeInfo = {
  firstName?: string | null
  lastName?: string | null
  employeeId?: string | null
}

export const childTokens: Record<string, TokenFn<ChildInfo>> = {
  VN1: (c) => take(c.firstName, 1),
  VN2: (c) => take(c.firstName, 2),
  VN3: (c) => take(c.firstName, 3),

  NN1: (c) => take(c.lastName, 1),
  NN2: (c) => take(c.lastName, 2),
  NN3: (c) => take(c.lastName, 3),

  ID: (c) => String(c.childId),
  ID3: (c) => pad(c.childId, 3),
  ID4: (c) => pad(c.childId, 4),

  JJMT: (c) => formatDate(c.birthdate, "YYYYMMDD"),
  JMT: (c) => formatDate(c.birthdate, "YYMMDD"),
  TMJJ: (c) => formatDate(c.birthdate, "DDMMYYYY"),
  TMJ: (c) => formatDate(c.birthdate, "DDMMYY"),
}

export const kibigChildIdMusterTokens: Record<string, TokenFn<ChildInfo>> = {
  ID: (c) => (c.childId != null ? String(c.childId) : ""),
  ID4: (c) => (c.childId != null ? pad(c.childId, 4) : "0000"),

  INI: (c) => getInitials(c.firstName, c.lastName),
  INI22: (c) => getInitials(c.firstName, c.lastName, 2),
}

export const kibigChildNameTokens: Record<string, TokenFn<ChildInfo>> = {
  ID: (c) => (c.childId != null ? String(c.childId) : ""),
  ID4: (c) => (c.childId != null ? pad(c.childId, 4) : "0000"),

  INI: (c) => getInitials(c.firstName, c.lastName, 1),
  INI22: (c) => getInitials(c.firstName, c.lastName, 2),

  NAME: (c) => c.lastName ?? "",
  KINDNAME: (c) => [c.lastName, c.firstName].filter(Boolean).join(" "),
  VORNAME: (c) => c.firstName ?? "",
}

export const employeeIdMusterTokens: Record<string, TokenFn<EmployeeInfo>> = {
  ID: (e) => e.employeeId ?? "",
  ID4: (e) => e.employeeId?.padStart(4, "0") ?? "0000",

  INI: (e) => getInitials(e.firstName, e.lastName, 1),
  INI22: (e) => getInitials(e.firstName, e.lastName, 2),
}
